import { DatePickerV2, Input, InputNumber, Radio, Select } from '@profgeosoft-ui/react';
import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import { useMemo, useState } from 'react';
import { StatusLabel } from 'src/features/status-label';
import { usePadPreparationStore } from 'src/pages/well-pad-preparation/use-pad-preparation-store';
import { SwitchButtonGroup } from 'src/shared/components/switch-button-group';
import { PadPreparation, RigSiteEvent } from 'src/store/well-pad-preparation/entities';

import styles from './pad-details.module.scss';

type Props = {
  pad: PadPreparation;
  isEditing: boolean;
};

export const PadDetails = observer<Props>(function PadDetails({ pad, isEditing }) {
  const { padTypeOptions } = usePadPreparationStore();

  const {
    padReadiness: { padReady, landReady, routeReady, routeDocumentationReady, electricityReady },
    constantValues: { unplannedWellCount },
    values: {
      landLeaseDate,
      forestProjectDate,
      padDocumentationComment,
      routeReadyDate,
      routeTypeId,
      routeComment,
      routeDocumentationDate,
      routeDocumentationComment,
      electricityDate,
      electricityComment,
    },
    rigSiteEvents,
  } = pad;

  const [activeRSE, setActiveRSE] = useState<RigSiteEvent>(rigSiteEvents[0]);

  const rigSiteEventSwitchButtonItems = useMemo(
    () =>
      rigSiteEvents
        .filter((rse) => rse.id !== 'blank')
        .map((rse, index) => ({ key: rse.id, title: `ПКБ ${index + 1}` })),
    [rigSiteEvents],
  );

  const {
    values: {
      costCenter,
      engineeringSurveysComment,
      engineeringSurveysCompleted,
      equipmentLayout,
      equipmentLayoutComment,
      mobilizationStartDate,
      padReadyDate,
      padTypeId,
      readinessComment,
      requiredWorks,
      wellCount,
    },
  } = activeRSE;

  const onActiveRSEChange = (key: string): void => {
    const newActiveRse = rigSiteEvents.find((rse) => rse.id === key);

    if (!newActiveRse) {
      throw new Error(`cannot find corrseponding RSE ${key}`);
    }

    setActiveRSE(newActiveRse);
  };

  return (
    <div className={styles.wrapper}>
      <SwitchButtonGroup value={activeRSE.id} items={rigSiteEventSwitchButtonItems} onChange={onActiveRSEChange} />

      <div className={styles.statusBlock}>
        <div className={styles.dataRow}>
          <div className={styles.statusBlockHeader}>
            <p className={styles.statusBlockHeader_text}>Готовность кустовой площадки</p>
            <StatusLabel status={padReady} />
          </div>
        </div>

        <div className={styles.dataRow}>
          <p className={styles.dataRow_label}>Дата готовности КП</p>
          <DatePickerV2
            value={padReadyDate}
            className={clsx(styles.field, !isEditing && styles.field_readOnly)}
            disabled={!isEditing}
          />
        </div>

        <div className={styles.dataRow}>
          <p className={styles.dataRow_label}>Дата начала мобилизации БУ на КП</p>
          <DatePickerV2
            value={mobilizationStartDate}
            className={clsx(styles.field, isEditing ? styles.field_disabled : styles.field_readOnly)}
            disabled
          />
        </div>

        <div className={styles.dataRow}>
          <p className={styles.dataRow_label}>Кол-во планируемых скважин на КП</p>
          <InputNumber
            value={wellCount}
            className={clsx(styles.field, isEditing ? styles.field_disabled : styles.field_readOnly)}
            disabled
          />
        </div>

        <div className={styles.dataRow}>
          <p className={styles.dataRow_label}>Кол-во незапланированных скважин на КП</p>
          <InputNumber
            value={unplannedWellCount}
            className={clsx(styles.field, isEditing ? styles.field_disabled : styles.field_readOnly)}
            disabled
          />
        </div>

        <div className={styles.dataRow}>
          <p className={styles.dataRow_label}>Тип КП</p>
          <Select
            value={padTypeId}
            options={padTypeOptions}
            className={clsx(styles.field, !isEditing && styles.field_readOnly)}
            disabled={!isEditing}
          />
        </div>

        <div className={styles.dataRow}>
          <p className={styles.dataRow_label}>Согласованная схема расстановки бурового оборудования</p>
          <div className={styles.valueBlock}>
            <label className={styles.radioWrapper}>
              <Radio
                checked={!equipmentLayout}
                className={clsx(!isEditing && styles.field_readOnly)}
                disabled={!isEditing}
              />
              <p>Отсутствует</p>
            </label>

            <label className={styles.radioWrapper}>
              <Radio
                checked={!!equipmentLayout}
                className={clsx(!isEditing && styles.field_readOnly)}
                disabled={!isEditing}
              />
              <p>В наличии</p>
            </label>

            <Input
              value={equipmentLayoutComment}
              className={clsx(styles.field, !isEditing && styles.field_readOnly)}
              disabled={!isEditing}
            />
          </div>
        </div>

        <div className={styles.dataRow}>
          <p className={styles.dataRow_label}>Инженерные геологические и геодезические изыскания</p>
          <div className={styles.valueBlock}>
            <label className={styles.radioWrapper}>
              <Radio
                checked={!engineeringSurveysCompleted}
                className={clsx(!isEditing && styles.field_readOnly)}
                disabled={!isEditing}
              />
              <p>Отсутствует</p>
            </label>

            <label className={styles.radioWrapper}>
              <Radio
                checked={!!engineeringSurveysCompleted}
                className={clsx(!isEditing && styles.field_readOnly)}
                disabled={!isEditing}
              />
              <p>В наличии</p>
            </label>

            <Input
              value={engineeringSurveysComment}
              className={clsx(styles.field, !isEditing && styles.field_readOnly)}
              disabled={!isEditing}
            />
          </div>
        </div>

        <div className={styles.dataRow}>
          <p className={clsx(styles.dataRow_label, styles.warningText)}>Место возникновения затрат</p>
          <Input
            value={costCenter}
            className={clsx(styles.field, !isEditing && styles.field_readOnly)}
            disabled={!isEditing}
          />
        </div>

        <div className={styles.dataRow}>
          <p className={styles.dataRow_label}>Ориентировочный перечень работ подлежащих выполнению/доп. работы</p>
          <Input
            value={requiredWorks}
            className={clsx(styles.field, styles.field__long, !isEditing && styles.field_readOnly)}
            disabled={!isEditing}
          />
        </div>

        <div className={styles.dataRow}>
          <p className={styles.dataRow_label}>Комментарий к готовности </p>
          <Input
            value={readinessComment}
            className={clsx(styles.field, styles.field__long, !isEditing && styles.field_readOnly)}
            disabled={!isEditing}
          />
        </div>
      </div>

      <div className={styles.statusBlock}>
        <div className={styles.dataRow}>
          <div className={styles.statusBlockHeader}>
            <p className={styles.statusBlockHeader_text}>Документация на кустовую площадку</p>
            <StatusLabel status={landReady} />
          </div>
        </div>

        <div className={styles.dataRow}>
          <p className={styles.dataRow_label}>Договор аренды на земельный участок</p>
          <div className={styles.valueBlock}>
            <DatePickerV2
              value={landLeaseDate}
              className={clsx(styles.field, !isEditing && styles.field_readOnly)}
              disabled={!isEditing}
            />
          </div>
        </div>

        <div className={styles.dataRow}>
          <p className={styles.dataRow_label}>Проект освоения лесов и лесной декларации</p>
          <div className={styles.valueBlock}>
            <DatePickerV2
              value={forestProjectDate}
              className={clsx(styles.field, !isEditing && styles.field_readOnly)}
              disabled={!isEditing}
            />
          </div>
        </div>

        <div className={styles.dataRow}>
          <p className={styles.dataRow_label}>Комментарий к готовности</p>
          <Input
            value={padDocumentationComment}
            className={clsx(styles.field, styles.field__long, !isEditing && styles.field_readOnly)}
            disabled={!isEditing}
          />
        </div>
      </div>

      <div className={styles.statusBlock}>
        <div className={styles.dataRow}>
          <div className={styles.statusBlockHeader}>
            <p className={styles.statusBlockHeader_text}>Готовность проезда</p>
            <StatusLabel status={routeReady} />
          </div>
        </div>

        <div className={styles.dataRow}>
          <p className={styles.dataRow_label}>Дата готовности</p>
          <DatePickerV2
            value={routeReadyDate}
            className={clsx(styles.field, !isEditing && styles.field_readOnly)}
            disabled={!isEditing}
          />
        </div>

        <div className={styles.dataRow}>
          <p className={styles.dataRow_label}>Тип проезда</p>
          <Select
            value={routeTypeId}
            className={clsx(styles.field, !isEditing && styles.field_readOnly)}
            disabled={!isEditing}
          />
        </div>

        <div className={styles.dataRow}>
          <p className={styles.dataRow_label}>Комментарий к готовности</p>
          <Input
            value={routeComment}
            className={clsx(styles.field, styles.field__long, !isEditing && styles.field_readOnly)}
            disabled={!isEditing}
          />
        </div>
      </div>

      <div className={styles.statusBlock}>
        <div className={styles.dataRow}>
          <div className={styles.statusBlockHeader}>
            <p className={styles.statusBlockHeader_text}>Документация на проезд</p>
            <StatusLabel status={routeDocumentationReady} />
          </div>
        </div>

        <div className={styles.dataRow}>
          <p className={styles.dataRow_label}>Документация на проезд</p>
          <div className={styles.valueBlock}>
            <DatePickerV2
              value={routeDocumentationDate}
              className={clsx(styles.field, !isEditing && styles.field_readOnly)}
              disabled={!isEditing}
            />
          </div>
        </div>

        <div className={styles.dataRow}>
          <p className={styles.dataRow_label}>Комментарий к готовности</p>
          <Input
            value={routeDocumentationComment}
            className={clsx(styles.field, styles.field__long, !isEditing && styles.field_readOnly)}
            disabled={!isEditing}
          />
        </div>
      </div>

      <div className={styles.statusBlock}>
        <div className={styles.dataRow}>
          <div className={styles.statusBlockHeader}>
            <p className={styles.statusBlockHeader_text}>Электроэнергия</p>
            <StatusLabel status={electricityReady} />
          </div>
        </div>

        <div className={styles.dataRow}>
          <p className={styles.dataRow_label}>Дата готовности</p>
          <DatePickerV2
            value={electricityDate}
            className={clsx(styles.field, !isEditing && styles.field_readOnly)}
            disabled={!isEditing}
          />
        </div>

        <div className={styles.dataRow}>
          <p className={styles.dataRow_label}>Комментарий к готовности</p>
          <Input
            value={electricityComment}
            className={clsx(styles.field, styles.field__long, !isEditing && styles.field_readOnly)}
            disabled={!isEditing}
          />
        </div>
      </div>
    </div>
  );
});
